/**
 * @file publicConfig.ts
 * @brief Links, contact details and company lines from one backend source:
 *        GET /api/public/config (BACKEND B23, ADR-057, 24-10-2026)
 *
 * @details
 * DES 24-10-2026: "change in one place, changes everywhere". Super Admin →
 * Links & company info saves the values; every link the web panel and the
 * Mac app show reads them from here.
 *
 * The page never depends on it: a missing endpoint (backend before B23), a
 * 404 / 429 / 503, bad JSON or a missing key all give the caller's fallback,
 * which is the value the code used before B23 (`FALLBACKS`). The server
 * leaves out empty values, so for an optional link (store, social) a missing
 * key means "hidden"; for a required one (terms, privacy, support) the
 * fallback is used, never a blank link.
 *
 * One fetch per page load (cached promise), refreshed after 5 minutes like
 * the server's own `Cache-Control: max-age=300`. `publicConfigSnapshot()`
 * is the last loaded value for code that cannot wait (a parser, an error
 * message), else null.
 *
 * Mac app (W19): `macUpdateRequired` is the one-gate rule from B23 for the
 * Mac shell: update when its version < MIN_APP_VERSION_MAC, compared
 * numerically per part; fail open (no version, no minimum, bad value → no
 * gate).
 *
 * W32 (30-10-2026): B42 adds `integrations: { zapier, make }` next to
 * `config` (live = server env AND the Super Admin platform switch). It is
 * kept as `INTEGRATION_LIVE_<APP>` booleans; `integrationLive` reads them.
 *
 * Pure (no `import.meta.env`) so node:test can load it.
 *
 * Related: usePublicConfig.ts, BACKEND src/features/appConfig/linkSettings.mjs
 *   + public.routes.mjs
 *
 * @author DES (prepared with Claude)
 * @date 24-10-2026
 */

export type PublicConfig = Record<string, string | boolean>;

/** What each place showed before B23: the value when the config is missing. */
export const FALLBACKS = {
  /** AppFooter "Help & Docs": the website's user guide. */
  HELP_URL: "https://verblizr.com/docs",
  TERMS_URL: "https://verblizr.com/terms/",
  PRIVACY_URL: "https://verblizr.com/privacy/",
  /** Plans → Enterprise "Contact sales" mailed support@ (.com) before B23. */
  SALES_EMAIL: "support@verblizr.com",
  /** The delete-account pages said support@verblizr.co.uk. */
  SUPPORT_EMAIL: "support@verblizr.co.uk",
  MAC_DOWNLOAD_URL: "https://verblizr.com/download/mac/",
} as const;

/** `{ success, config: {…} }` → the config, or null when it is not one. */
export function parsePublicConfig(raw: unknown): PublicConfig | null {
  if (!raw || typeof raw !== "object") return null;
  const cfg = (raw as { config?: unknown }).config;
  if (!cfg || typeof cfg !== "object" || Array.isArray(cfg)) return null;
  const out: PublicConfig = {};
  for (const [k, v] of Object.entries(cfg as Record<string, unknown>)) {
    if (typeof v === "string") {
      if (v.trim()) out[k] = v.trim();
    } else if (typeof v === "boolean") {
      out[k] = v;
    }
  }
  const integrations = (raw as { integrations?: unknown }).integrations;
  if (integrations && typeof integrations === "object" && !Array.isArray(integrations)) {
    for (const [app, live] of Object.entries(integrations as Record<string, unknown>)) {
      if (typeof live === "boolean") out[integrationKey(app)] = live;
    }
  }
  return out;
}

const integrationKey = (app: string) => `INTEGRATION_LIVE_${app.toUpperCase()}`;

/**
 * W32: is this automation app (zapier, make) on for the platform? Only an
 * explicit `false` from B42 is "off"; no config or an older backend reads as
 * on, because the server gates are the real enforcement.
 */
export function integrationLive(cfg: PublicConfig | null | undefined, app: string): boolean {
  return cfg?.[integrationKey(app)] !== false;
}

/** A string value, or `fallback` when the config or the key is missing. */
export function configValue(cfg: PublicConfig | null | undefined, key: string, fallback: string): string {
  const v = cfg?.[key];
  return typeof v === "string" && v ? v : fallback;
}

/** An optional value: "" when the config has none (hidden), `fallback` only without a config. */
export function optionalValue(cfg: PublicConfig | null | undefined, key: string, fallback = ""): string {
  if (!cfg) return fallback;
  const v = cfg[key];
  return typeof v === "string" ? v : "";
}

/** Only https links are ever put in an href (the server checks too). */
export function safeHttps(url: string, fallback: string): string {
  try {
    return new URL(url).protocol === "https:" ? url : fallback;
  } catch {
    return fallback;
  }
}

/** "mailto:sales@…?subject=…" with the subject encoded. */
export function mailto(email: string, subject?: string): string {
  return `mailto:${email}${subject ? `?subject=${encodeURIComponent(subject)}` : ""}`;
}

/** "1.4.2" → [1, 4, 2]; null when it is not a version. */
export function versionParts(v: string | null | undefined): number[] | null {
  const t = String(v ?? "").trim();
  if (!/^\d+(\.\d+)*$/.test(t)) return null;
  return t.split(".").map((p) => parseInt(p, 10));
}

/** -1 / 0 / 1, numerically per part ("1.10" > "1.9"; "1.4" = "1.4.0"); null if either is not a version. */
export function compareVersions(a: string, b: string): number | null {
  const x = versionParts(a);
  const y = versionParts(b);
  if (!x || !y) return null;
  for (let i = 0; i < Math.max(x.length, y.length); i++) {
    const d = (x[i] ?? 0) - (y[i] ?? 0);
    if (d !== 0) return d < 0 ? -1 : 1;
  }
  return 0;
}

/** B23 one gate for the Mac shell. Fail open: anything unknown = no gate. */
export function macUpdateRequired(shellVersion: string | null | undefined, minVersion: string | null | undefined): boolean {
  if (!shellVersion || !minVersion) return false;
  return compareVersions(shellVersion, minVersion) === -1;
}

// ---------------------------------------------------------------------------
// Loader: one request per page load, shared by every caller.
// ---------------------------------------------------------------------------

type FetchLike = (url: string, init?: RequestInit) => Promise<Response>;

const MAX_AGE_MS = 5 * 60 * 1000;
let snapshot: PublicConfig | null = null;
let inflight: { at: number; promise: Promise<PublicConfig | null> } | null = null;

/** The last config loaded in this page, or null (not loaded / failed). */
export function publicConfigSnapshot(): PublicConfig | null {
  return snapshot;
}

/** GET {base}/api/public/config; never throws (null on any failure). */
export function loadPublicConfig(baseUrl: string, fetchImpl: FetchLike = (u, i) => fetch(u, i)): Promise<PublicConfig | null> {
  if (inflight && Date.now() - inflight.at < MAX_AGE_MS) return inflight.promise;
  const promise = (async () => {
    try {
      const res = await fetchImpl(`${baseUrl.replace(/\/$/, "")}/api/public/config`, {
        credentials: "omit",
        headers: { Accept: "application/json" },
      });
      if (!res.ok) return snapshot;
      const parsed = parsePublicConfig(await res.json());
      if (parsed) snapshot = parsed;
      return snapshot;
    } catch {
      return snapshot;
    }
  })();
  inflight = { at: Date.now(), promise };
  return promise;
}

/** Tests and the dev preview: forget what was loaded. */
export function __resetPublicConfig(next: PublicConfig | null = null): void {
  snapshot = next;
  inflight = next ? { at: Date.now(), promise: Promise.resolve(next) } : null;
}
